import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Play } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
    >
      {/* Ambient gradient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] rounded-full bg-[#D4A94F]/10 blur-[120px]" />
        <div className="absolute bottom-0 -right-32 w-[600px] h-[600px] rounded-full bg-[#B8892C]/8 blur-[130px]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_75%)]" />
      </div>

      {/* Subtle grid */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 fade-up">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-8">
            <Sparkles className="w-3.5 h-3.5 text-[#E9C46A]" />
            <span className="text-xs font-medium tracking-wider uppercase text-[#E9C46A]">
              Distribusi Musik Indonesia
            </span>
          </div>

          <h1 className="text-white font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6">
            Rilis Musikmu ke
            <br />
            <span className="gold-gradient-text">Seluruh Dunia</span>
          </h1>

          <p className="text-neutral-300 text-lg md:text-xl max-w-2xl leading-relaxed mb-10">
            Distribusikan lagu Anda ke{' '}
            <span className="text-white font-semibold">Spotify, Apple Music, JOOX, TikTok, YouTube Music</span>{' '}
            dan 20+ platform digital lainnya dengan cepat, mudah, dan royalti 100% milik artis.
          </p>

          <div className="flex flex-wrap gap-4 items-center">
            <Button
              asChild
              className="h-14 px-8 rounded-full bg-gradient-to-r from-[#E9C46A] to-[#D4A94F] text-black font-semibold text-base btn-transition gold-shimmer hover:shadow-[0_10px_40px_-10px_rgba(212,169,79,0.6)]"
            >
              <Link to="/rilis-musik">
                Mulai Rilis
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>

          {/* Trust indicator */}
          <div className="mt-14 flex items-center gap-4">
            <div className="flex -space-x-2">
              {[
                'https://www.werdimedia.com/wp-content/uploads/2020/11/DAY-IDAY-Curang-More-800x750.jpg',
                'https://www.werdimedia.com/wp-content/uploads/2020/08/Fausta-Salu-800x750.jpg',
                'https://www.werdimedia.com/wp-content/uploads/2020/08/Bayuga-Isih-Payu-800x750.jpg',
                'https://www.werdimedia.com/wp-content/uploads/2021/04/Rangkullah-Hatiku-Uchi-Qolby-Werdi-Media-800x750.jpg',
              ].map((src, i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full border-2 border-black overflow-hidden bg-neutral-800"
                >
                  <img src={src} alt="artist" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <div>
              <p className="text-white font-semibold text-sm">1.200+ Artis Percaya</p>
              <p className="text-neutral-400 text-xs">Bergabunglah dengan komunitas musisi kami</p>
            </div>
          </div>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: '0.2s' }}>
          <div className="relative aspect-square max-w-md mx-auto">
            {/* Rotating vinyl */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#1a1a1a] via-black to-[#111] shadow-[0_25px_60px_-15px_rgba(212,169,79,0.4)] animate-spin-slow">
              <div className="absolute inset-4 rounded-full border border-neutral-800" />
              <div className="absolute inset-8 rounded-full border border-neutral-800" />
              <div className="absolute inset-14 rounded-full border border-neutral-800" />
              <div className="absolute inset-20 rounded-full border border-neutral-800" />
              <div className="absolute inset-0 rounded-full flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#E9C46A] to-[#B8892C] flex items-center justify-center shadow-inner">
                  <div className="w-6 h-6 rounded-full bg-black" />
                </div>
              </div>
            </div>

            {/* Floating cards */}
            <div className="absolute -top-6 -right-2 md:-right-8 bg-[#111] border border-[#D4A94F]/30 rounded-2xl p-4 shadow-2xl backdrop-blur-md">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1DB954] to-[#0f7a35] flex items-center justify-center">
                  <Play className="w-4 h-4 text-white fill-white" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Live on Spotify</p>
                  <p className="text-neutral-400 text-xs">+2.4M streams today</p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -left-2 md:-left-8 bg-[#111] border border-[#D4A94F]/30 rounded-2xl p-4 shadow-2xl backdrop-blur-md">
              <p className="text-[10px] tracking-wider text-[#D4A94F] uppercase font-semibold mb-1">
                Royalti Bulan Ini
              </p>
              <p className="text-white font-bold text-lg">Rp 12.4Jt</p>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-[10px] text-emerald-400 font-semibold">↑ 24%</span>
                <span className="text-[10px] text-neutral-500">vs last month</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
